import { computed, toValue, watch, type ComputedRef, type MaybeRefOrGetter, type Ref, type WritableComputedRef } from "vue";
import { useField, type TypedSchema } from "vee-validate";
import type { z } from "zod";
import { toTypedSchema } from "./toTypedSchema";

export interface OptionalFieldBinding<TValue> {
  /** The value the input should render/edit (form value when registered, otherwise the plain v-model) */
  value: Ref<TValue | undefined> | WritableComputedRef<TValue | undefined>;
  /** First validation error to display, if any */
  errorMessage: ComputedRef<string | undefined>;
  /** Bind to the input's `blur` event: marks the field as touched and validates it */
  onBlur: (event?: Event) => void;
  /** Call on user edits when the input writes the model directly instead of `value` (shows errors while typing) */
  touch: () => void;
}

/**
 * Lets an input wrapper component (e.g. `TextfieldPlus`) optionally take part in VeeValidate.
 *
 * - Without `name` the component is a plain v-model input and this is a pass-through.
 * - With `name` the field registers itself (via `useField`) with the closest `useForm()`, so the form's
 *   Zod schema validates it, `meta.valid`/`handleSubmit` include it, and errors are shown under it.
 * - With `name` + `schema` the field is standalone: it validates against its own schema and never joins
 *   an ancestor form (even if one exists further up, e.g. a dialog form around an editor), so it can't
 *   block or be silenced by a form it doesn't belong to.
 * - A bound v-model is kept in sync with the field value in both directions, so existing state can stay
 *   the source of truth.
 *
 * Validation timing mimics Vuetify: a field stays quiet until the user edits it, leaves it or submits the
 * form, and from then on re-validates on every change.
 *
 * Must be called during `setup()`. Whether a `name` is set is decided once at setup; the path itself may change
 * later (e.g. `rows[${index}].name` in a keyed `v-for`), so pass a getter.
 *
 * @param name VeeValidate field path (e.g. `url` or `params[2].name`), or a getter returning it
 * @param model the component's v-model (`defineModel()`); `undefined` means "not bound"
 * @param schema optional field-level Zod schema; makes the field standalone (see above)
 */
export function useOptionalField<TValue>(
  name: MaybeRefOrGetter<string | undefined>,
  model: Ref<TValue | undefined>,
  schema?: z.ZodType,
): OptionalFieldBinding<TValue> {
  if (!toValue(name)) {
    return {
      value: model,
      errorMessage: computed(() => undefined),
      onBlur: () => {},
      touch: () => {},
    };
  }

  const field = useField<TValue | undefined>(() => toValue(name) ?? "", schema ? (toTypedSchema(schema) as TypedSchema<TValue | undefined>) : undefined, {
    // Only seed the field from v-model if one is bound; otherwise the form's initialValues win
    ...(model.value !== undefined ? { initialValue: model.value } : {}),
    validateOnValueUpdate: false,
    // A field with its own schema is self-contained: don't register it with an ancestor form
    controlled: !schema,
  });

  watch(model, (newValue) => {
    if (!Object.is(newValue, field.value.value)) field.value.value = newValue;
  });

  watch(field.value, (newValue) => {
    if (!Object.is(newValue, model.value)) model.value = newValue;
    if (field.meta.touched) void field.validate();
  });

  return {
    // Writes come from the input (user edits): they mark the field as touched, so its error shows while typing
    value: computed({
      get: () => field.value.value,
      set: (newValue) => {
        field.value.value = newValue;
        if (!field.meta.touched) field.setTouched(true);
      },
    }),
    errorMessage: computed(() => field.errorMessage.value),
    onBlur: (event?: Event) => field.handleBlur(event, true),
    touch: () => {
      if (!field.meta.touched) field.setTouched(true);
    },
  };
}
